// Discover-tab reports, persisted per tenant.
//
// localStorage for the same reason the wiki store starts there: a report you
// can generate, read, and regenerate in one sitting proves the surface, and
// plain versioned JSON makes a later move to the backend a transport change.
// One report per scopeKey — a rerun replaces the scope's report in place. A
// report records the range it was run over (meta.rangeText) rather than
// invalidating on a range change, the same stance the wiki takes.

import { browserStorageKey, discardLegacyTenantEntry } from "./browserStorageScope.js";

const STORAGE_KEY_PREFIX = "origin.anatomy.discover.v2";
const LEGACY_STORAGE_KEY_PREFIX = "origin.anatomy.discover.v1";

// Reports otherwise accumulate one per scope ever profiled; past the cap the
// oldest go first (localStorage write failures are the worst failure mode
// this store has — see the wiki store's growth bounds).
export const MAX_REPORTS = 24;

let _storageOverride = null;
function storage() {
    if (_storageOverride) return _storageOverride;
    return typeof localStorage === "undefined" ? null : localStorage;
}

/** Test-only: supply a storage backend. Pass null to fall back to the global. */
export function _setStorageForTests(s) { _storageOverride = s; }

function storageKey(tenantId) {
    discardLegacyTenantEntry(storage(), LEGACY_STORAGE_KEY_PREFIX, tenantId);
    return browserStorageKey(STORAGE_KEY_PREFIX, tenantId);
}

const listeners = new Set();
function notify() {
    for (const fn of listeners) {
        try { fn(); } catch { /* one listener's failure must not block its siblings */ }
    }
}

/** Subscribe to discovery-store changes. Returns an unsubscribe. */
export function subscribeDiscovery(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

const EMPTY = { reports: {} };

/**
 * The tenant's discovery reports, keyed by scopeKey. Each entry is
 * { report, trail, meta: { at, scopeText, rangeText, model } }.
 * Never throws; a corrupt bucket reads as empty.
 */
export function readDiscovery(tenantId) {
    try {
        const bucketKey = storageKey(tenantId);
        if (!bucketKey) return EMPTY;
        const raw = storage()?.getItem(bucketKey);
        if (!raw) return EMPTY;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || !parsed.reports || typeof parsed.reports !== "object") {
            return EMPTY;
        }
        return { reports: parsed.reports };
    } catch {
        return EMPTY;
    }
}

/**
 * Store one scope's report, evicting the oldest entries past MAX_REPORTS
 * (never the one just written). Returns { ok, problems }.
 */
export function saveDiscoveryReport(tenantId, scopeKey, entry) {
    const bucketKey = storageKey(tenantId);
    if (!bucketKey) return { ok: false, problems: ["verified tenant and user identity are required"] };
    const key = scopeKey || "org";
    const current = readDiscovery(tenantId);
    const reports = { ...current.reports, [key]: entry };
    const keys = Object.keys(reports);
    if (keys.length > MAX_REPORTS) {
        const evictable = keys
            .filter((k) => k !== key)
            .sort((a, b) => (reports[a]?.meta?.at || 0) - (reports[b]?.meta?.at || 0));
        for (const k of evictable.slice(0, keys.length - MAX_REPORTS)) delete reports[k];
    }
    try {
        storage()?.setItem(bucketKey, JSON.stringify({ reports }));
    } catch (err) {
        // Quota pressure: the trace body is the bulk and is additive — retry
        // once without it so the report itself always survives.
        if (entry?.traceBody) {
            const { traceBody, ...lean } = entry;
            const retry = saveDiscoveryReport(tenantId, key, lean);
            if (retry.ok) {
                return { ok: true, problems: ["trace body dropped to fit storage — Open as trace unavailable for this run"] };
            }
        }
        return { ok: false, problems: [`could not persist: ${err?.message || String(err)}`] };
    }
    notify();
    return { ok: true, problems: [] };
}

/** Test-only: drop a tenant's bucket and all listeners. */
export function _resetForTests(tenantId) {
    try { const key = storageKey(tenantId); if (key) storage()?.removeItem(key); } catch { /* noop */ }
    listeners.clear();
}
